import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { euro } from "@/cart";

const API = process.env.REACT_APP_BACKEND_URL;

const perkLabel = (perk) =>
  perk?.percent_off ? `${perk.percent_off}%` : perk?.amount_off_cents ? euro(perk.amount_off_cents) : null;

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 },
  }),
};

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [perk, setPerk] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/newsletter/perk`)
      .then((r) => (r.ok ? r.json() : { perk: null }))
      .then((d) => setPerk(d.perk))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError("Inserisci un indirizzo email valido.");
      return;
    }
    setState("loading");
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value, source: "website" }),
      });
      if (!res.ok) throw new Error("request failed");
      const data = await res.json();
      setState("done");
      toast(
        data.status === "already"
          ? "Sei già dei nostri."
          : "Benvenuto nella Linea.",
        { description: value }
      );
    } catch {
      setState("idle");
      setError("Qualcosa è andato storto. Riprova tra poco.");
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative overflow-hidden border-t border-red-950/50 bg-[#0A0A0A]"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_80%_50%,rgba(200,16,46,0.2),transparent_60%)]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-28 sm:px-8 sm:py-36 lg:grid-cols-[1fr_1fr] lg:items-center">
        <div>
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="font-mono-tac text-xs uppercase tracking-[0.35em] text-red-600"
          >
            09 — Newsletter
          </motion.p>
          <motion.h2
            variants={reveal}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="font-display mt-4 text-5xl uppercase leading-[0.95] text-neutral-100 sm:text-7xl"
          >
            Resta sulla <span className="text-[#C8102E]">Linea</span>
          </motion.h2>
          <motion.p
            variants={reveal}
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="mt-6 max-w-md text-base leading-relaxed text-neutral-400"
          >
            Nuove date, uscite in anteprima e quello che non postiamo da
            nessun'altra parte. Niente spam: scriviamo solo quando c'è rumore
            da fare.
          </motion.p>
          {perkLabel(perk) && (
            <motion.p
              data-testid="newsletter-perk"
              variants={reveal}
              custom={3}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="mt-6 inline-flex items-center gap-3 border border-red-900/60 bg-[#0D0D0D] px-4 py-3 text-sm text-neutral-200"
            >
              <Gift className="h-4 w-4 shrink-0 text-red-600" />
              <span>
                Iscriviti e ricevi subito{" "}
                <strong className="text-red-500">{perkLabel(perk)} di sconto</strong> sul
                tuo primo ordine nel Merch.
              </span>
            </motion.p>
          )}
        </div>

        <motion.div
          variants={reveal}
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {state === "done" ? (
            <div
              data-testid="newsletter-success"
              className="border border-red-900/50 bg-[#0D0D0D] p-8"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center bg-[#C8102E] text-white">
                <Check className="h-5 w-5" />
              </span>
              <h3 className="font-display mt-5 text-3xl uppercase text-neutral-100">
                Sei dentro.
              </h3>
              <p className="mt-2 text-sm text-neutral-400">
                Ti abbiamo appena mandato un'email di benvenuto (controlla
                anche lo spam).{" "}
                {perkLabel(perk)
                  ? `Dentro trovi il tuo codice sconto del ${perkLabel(perk)} per il Merch.`
                  : "Ti scriviamo alla prossima data o alla prossima uscita."}
              </p>
            </div>
          ) : (
            <form
              data-testid="newsletter-form"
              onSubmit={submit}
              noValidate
              className="border border-neutral-800 bg-[#0D0D0D] p-3"
            >
              <label
                htmlFor="newsletter-email"
                className="block px-3 pt-3 font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500"
              >
                La tua email
              </label>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                  id="newsletter-email"
                  data-testid="newsletter-email-input"
                  type="email"
                  autoComplete="email"
                  placeholder="nome@esempio.it"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={state === "loading"}
                  className="h-14 flex-1 border border-transparent bg-black/40 px-4 text-base text-neutral-100 placeholder:text-neutral-600 focus:border-red-700 focus:outline-none"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit-button"
                  disabled={state === "loading"}
                  className="inline-flex h-14 items-center justify-center gap-3 bg-[#C8102E] px-6 font-mono-tac text-sm uppercase tracking-[0.2em] text-white transition-all duration-200 hover:bg-[#FF2A4B] hover:shadow-[0_0_30px_rgba(200,16,46,0.5)] disabled:opacity-60"
                >
                  {state === "loading" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Iscrivimi
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
              <p
                data-testid="newsletter-error"
                role="alert"
                className={`min-h-[1.25rem] px-3 pb-2 pt-3 text-xs ${
                  error ? "text-red-500" : "text-neutral-600"
                }`}
              >
                {error ||
                  "Iscrivendoti accetti di ricevere le nostre email. Puoi cancellarti quando vuoi."}
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
